import { router, initRipple } from '@codesuma/baseline'
import { App } from './app'
import hijriAdjustments from './services/hijri-adjustments'
import { openStorage, retryWrites, useLocalEngine } from './services/storage'
import tasks from './services/tasks'
import events from './services/events'
import notes from './services/notes'
import occasionOverrides from './services/occasion-overrides'
import dayColors from './services/day-colors'
import reminders from './services/reminders'
import calendar from './services/calendar'
import theme from './services/theme'
import widget from './services/widget'
import countryOccasions from './services/country-occasions'
import phoneCalendar from './services/phone-calendar'
import phoneReminders from './services/phone-reminders'
import adoptionScan from './services/event-adoption-scan'
import appUpdate from './services/app-update'
import rating from './services/rating'

async function boot() {
    initRipple() // one-time: elements opt in with the data-ripple attribute
    theme.init() // paints <html> with the saved theme before anything renders
    // Before the first render: applies the saved moon-sighting corrections
    // synchronously, then checks the server for newer ones in the background.
    hijriAdjustments.init()
    await openStorage()
    // A first read that throws — the engine opened but the query failed —
    // must not leave a blank app. Refilling from localStorage cannot throw,
    // so the user always sees their data, and the next launch retries.
    const fill = () => Promise.all([tasks.init(), events.init(), notes.init(), occasionOverrides.init(), dayColors.init()])
    await fill().catch(async err => {
        console.warn('storage: falling back to local', err)
        useLocalEngine()
        await fill()
    })
    // Before the first render, not after: the catalog is read from storage
    // synchronously, and a day opened straight away has to be drawn with the
    // countries the user added rather than filling them in a beat later.
    countryOccasions.init()
    document.body.appendChild(App().el)
    // After the app exists: registering reminders can route, since tapping a
    // notification opens the day it is about, and the pages have to be listening
    // before the calendar can tell them the day has turned.
    reminders.init()
    calendar.init()
    // Native only: hands the home-screen widget the days it draws.
    widget.init()
    // Native only: drops what it holds when the phone's calendar may have changed
    // under the app, and when the switch that shows it is turned off.
    phoneCalendar.init()
    // iPhone only: the same for the reminders read from the Reminders app.
    phoneReminders.init()
    // Native only: looks once a launch for events in the phone's calendar that are
    // really anniversaries, and offers to take them over.
    adoptionScan.init()
    // Native only: asks the store whether a newer version is out, and raises
    // the update notice if so.
    appUpdate.init()
    // Native only: counts the days the app is used on, so the rating prompt
    // waits for someone who keeps coming back.
    rating.init()
    // A write the store refused earlier gets another go whenever the app comes
    // back to the front — a disk that was full or busy then may take it now.
    // The same three moments the calendar watches for a new day.
    const retry = () => { retryWrites() }
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') retry()
    })
    window.addEventListener('focus', retry)
    // Reached through the global rather than the const below, which is still in
    // its temporal dead zone while boot is running.
    ;(window as any).Capacitor?.Plugins?.App?.addListener?.(
        'appStateChange',
        ({ isActive }: { isActive: boolean }) => { if (isActive) retry() })
}

boot()

// Route the Android system back gesture/button through the app's router so it
// navigates back with the app's own transition.
const cap = (window as any).Capacitor
if (cap?.isNativePlatform?.()) {
    cap.Plugins?.App?.addListener('backButton', () => {
        if (router.getPath() === '/') cap.Plugins?.App?.exitApp()
        else router.back()
    })
}
